import {useAssetDetails} from "../../../../context/assetDetails/AssetDetails";
import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import {PaginationConfig} from "antd/lib/pagination";
import {
  BiodiversityColumns,
  BiodiversityFiltersFE,
  BiodiversitySortersFE,
  fromFeToBeBiodiversityTableFilters,
} from "../../../../types/internal/tables/biodiversityTable";
import {useGetAssetBiodiversityPaginatedQuery} from "../../../../redux/api/asset";
import {capitalize, cloneDeep, isEmpty} from "lodash";
import {fromFeToBeSorter} from "../../../../utils/parser";
import {skipToken} from "@reduxjs/toolkit/query";
import {Sorter} from "../../../../types/internal/tables/sorters";
import {DocumentsTableColumnHeader} from "../../../documents/TablesViews/DocumentsTableColumnHeader";
import TableSorter from "../../../common/TableSorter";
import {Pagination, Table, Tag, Typography} from "antd";
import {BiodiversityInput} from "../../../assets/AssetsTable/filters/BiodiversityDistance";
import {AssetBiodiversity, BIODIVERSITY_SOURCE} from "../../../../types/api/assets";
import CoordinatesAssetMissing from "../../../common/CoordinatesAssetMissing";
import CustomNumericFormat from "../../../common/customNumeric/CustomNumericFormat";
import {ColumnsType} from "antd/lib/table";
import HelpTooltip from "../../../common/HelpTooltip";
import BiodiversityRelatedOptionsFilter from "./BiodiversityRelatedOptionsFilter";
import DonutChart from "./DonutChart";

export default function BiodiversityNatureTable() {
  const [_, {asset}] = useAssetDetails()
  const {t} = useTranslation()
  const [pagination, setPagination] = useState<PaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showTotal: (total, range) => t('table.pageSizeOfTotal', {
      rangeStart: range[0],
      rangeEnd: range[1],
      total,
    }),
    onChange: (page, pageSize) => {
      setPagination(prevPag => ({...prevPag, current: page, pageSize}))
    },
    style: {textAlign: 'center', marginTop: '1rem'},
  })
  const [sorters, setSorters] = useState<{ [column in BiodiversityColumns]: Sorter }>({
    area_km2: null,
    data_source: null,
    designation: null,
    designation_eng: null,
    distance: null,
    name: null,
    pa_id: null,
    designation_type: null,
    coverage: null,
  })
  const [filters, setFilters] = useState<BiodiversityFiltersFE>({})
  const isMissingCoordinates = asset?.position === null

  const {
    data,
    isFetching,
    isUninitialized,
  } = useGetAssetBiodiversityPaginatedQuery(asset?.uuid && !isMissingCoordinates ? {
    assetUuid: asset.uuid,
    page: pagination.current,
    page_size: pagination.pageSize,
    ...fromFeToBeBiodiversityTableFilters(filters),
    ...(!isEmpty(fromFeToBeSorter(sorters)) ? {
      ordering: fromFeToBeSorter(sorters),
    } : {}),
  } : skipToken)

  const handleUpdateSorter = (sorter: keyof BiodiversitySortersFE, value: Sorter) => {
    if (value) {
      setSorters(prevState => ({
        ...prevState,
        [sorter]: value
      }))
    } else {
      setSorters(prevState => {
        const updatedState = cloneDeep(prevState)
        delete updatedState[sorter]
        return updatedState
      })
    }
  }

  const onRemoveFilter = (filter: (keyof BiodiversityFiltersFE)) => {
    setFilters(prevState => {
      const copyState = cloneDeep(prevState)
      delete copyState[filter]
      return copyState
    })
    setPagination(prevPagination => ({...prevPagination, current: 1}))
  }

  const onUpdateFilter = (updatedFilters: BiodiversityFiltersFE) => {
    setFilters(prevState => ({...prevState, ...updatedFilters}))
    setPagination(prevPagination => ({...prevPagination, current: 1}))
  }
  const distance = filters['distance'] === undefined ? "" : filters['distance']
  const coverage = filters['coverage'] === undefined ? "" : filters['coverage']

  const columns: ColumnsType<AssetBiodiversity> = [
    {
      key: "data_source",
      dataIndex: "data_source",
      title: <DocumentsTableColumnHeader
        title={capitalize(t("assets.landCoverageDataSource"))}
        filter={asset.uuid && <BiodiversityRelatedOptionsFilter
          filters={filters}
          setFilters={(params) => {
            setFilters(params)
            setPagination(prevState => ({...prevState, current: 1}))
          }}
          field={"data_source"}
          assetUuid={asset.uuid}
        />}
        onFilterReset={() => onRemoveFilter('data_source')}
        isFilterDisabled={filters.data_source === undefined}
      />,
      render: (v, record) => {
        const color = record.data_source === BIODIVERSITY_SOURCE.WDPA ? 'green' : record.data_source === BIODIVERSITY_SOURCE.CORINE ? 'blue' : undefined
        return <Tag color={color}>{v}</Tag>
      }
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: <DocumentsTableColumnHeader
        title={capitalize(t('assets.biodiversityName'))}
        sorter={<TableSorter
          value={sorters.name}
          onChange={type => setSorters(prevState => ({...prevState, name: type}))}
        />}
        filter={asset.uuid && <BiodiversityRelatedOptionsFilter
          filters={filters}
          setFilters={(params) => {
            setFilters(params)
            setPagination(prevState => ({...prevState, current: 1}))
          }}
          field={"name"}
          assetUuid={asset.uuid}
        />}
        onFilterReset={() => onRemoveFilter('name')}
        isFilterDisabled={filters.name === undefined}
      />
    },
    {
      key: 'designation',
      dataIndex: 'designation',
      title: capitalize(t('assets.biodiversityDesignation'))
    },
    {
      key: 'designation_eng',
      dataIndex: 'designation_eng',
      title: capitalize(t('assets.biodiversityDesignationEng'))
    },
    {
      key: 'designation_type',
      dataIndex: 'designation_type',
      title: <DocumentsTableColumnHeader
        title={capitalize(t('assets.biodiversityDesignationType'))}
        filter={asset.uuid && <BiodiversityRelatedOptionsFilter
          filters={filters}
          setFilters={(params) => {
            setFilters(params)
            setPagination(prevState => ({...prevState, current: 1}))
          }}
          field={"designation_type"}
          assetUuid={asset.uuid}
        />}
        onFilterReset={() => onRemoveFilter('designation_type')}
        isFilterDisabled={filters.designation_type === undefined}
      />
    },
    {
      key: 'pa_id',
      dataIndex: 'pa_id',
      title: <DocumentsTableColumnHeader
        title={'ID'}
        filter={asset.uuid && <BiodiversityRelatedOptionsFilter
          filters={filters}
          setFilters={(params) => {
            setFilters(params)
            setPagination(prevState => ({...prevState, current: 1}))
          }}
          field={"pa_id"}
          assetUuid={asset.uuid}
        />}
        onFilterReset={() => onRemoveFilter('pa_id')}
        isFilterDisabled={filters.pa_id === undefined}
      />
    },
    {
      key: 'distance',
      dataIndex: 'distance',
      title: <DocumentsTableColumnHeader
        title={capitalize(t('assets.biodiversityDistance'))}
        sorter={<TableSorter
          value={sorters.distance || null}
          onChange={(v) => handleUpdateSorter('distance', v)}
        />}
        filter={<div style={{maxWidth: '15rem', display: 'flex', gap: '0.5rem', flexDirection: 'column'}}>
          <Typography.Text>{capitalize(t('assets.biodiversityMaxDistance'))}</Typography.Text>
          <BiodiversityInput
            value={distance}
            onValueChange={({floatValue}) => {
              if (typeof floatValue === 'number' && floatValue >= 0) {
                onUpdateFilter({distance: floatValue})
              } else {
                onRemoveFilter('distance')
              }
            }}
          />
        </div>}
        onFilterReset={() => onRemoveFilter('distance')}
        isFilterDisabled={filters.distance === undefined}
      />,
      render: v => <CustomNumericFormat
        value={v}
        displayType={'text'}
        suffix={" m"}
        decimalScale={0}
        fixedDecimalScale={true}
      />,
      align: 'right'
    },
    {
      key: "area_km2",
      dataIndex: "area_km2",
      title: capitalize(t("assets.landCoverageAreaKm2")),
      render: v => <CustomNumericFormat
        value={v}
        displayType={'text'}
        suffix={" km²"}
        decimalScale={2}
        fixedDecimalScale={true}
      />,
      align: 'right'
    },
    {
      key: "coverage",
      dataIndex: "coverage",
      title: <DocumentsTableColumnHeader
        title={<>
          {capitalize(t("assets.landCoveragePercentage"))}
          <HelpTooltip tooltip={{title: capitalize(t("assets.biodiversityCoverageInfo"))}}
                       iconStyle={{fontSize: '', marginLeft: '0.2rem'}}/>
        </>}
        sorter={<TableSorter
          value={sorters.coverage || null}
          onChange={(v) => handleUpdateSorter('coverage', v)}
        />}
        filter={<div style={{maxWidth: '15rem', display: 'flex', gap: '0.5rem', flexDirection: 'column'}}>
          <Typography.Text>{capitalize(t('assets.landCoverageMin'))}</Typography.Text>
          <BiodiversityInput
            value={coverage}
            onValueChange={({floatValue}) => {
              if (typeof floatValue === 'number' && floatValue >= 0) {
                onUpdateFilter({coverage: floatValue})
              } else {
                onRemoveFilter('coverage')
              }
            }}
            placeholder={capitalize(t("assets.landCoverageMin"))}
            suffix={" %"}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        </div>}
        onFilterReset={() => onRemoveFilter('coverage')}
        isFilterDisabled={filters.coverage === undefined}
      />,
      render: v => <CustomNumericFormat
        value={v * 100}
        displayType={'text'}
        suffix={" %"}
        decimalScale={2}
        fixedDecimalScale={true}
      />,
      align: 'right'
    },
  ]

  const coverageData = asset.overall_land_coverage.filter(el => el.coverage !== null)

  return <>
    {isMissingCoordinates ?
      <CoordinatesAssetMissing/>
      :
      <>
        {!!coverageData.length && <>
          <Typography.Title level={5} style={{
            textAlign: 'center',
            margin: "1rem 0 0 0"
          }}>{capitalize(t("assets.landCoverageTotal"))}</Typography.Title>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            justifyContent: 'space-around'
          }}>
            {coverageData
              .map(el => <DonutChart
                key={el.data_source}
                percentage={(el.coverage ?? 0) * 100}
                title={el.data_source}
              />)}
          </div>
        </>}
        <Table
          <AssetBiodiversity>
          loading={(isFetching || isUninitialized) && !isMissingCoordinates}
          columns={columns}
          pagination={false}
          scroll={{x: true}}
          rowKey={'uuid'}
          dataSource={data?.results}
        />
        {!!data?.count && <Pagination
          {...pagination}
          total={data?.count}
        />}
      </>
    }
  </>
}